import RenderManager from "../rendering/render-manager";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";
import SlideManager from "../utils/slide-manager";
import Base2DElement from "./base-2d-element";
import Transition from "./Transition";
import Utils from "../utils.js";

class Video extends Base2DElement {
    /**
     * @type {HTMLVideoElement}
     */
    video = null;

    get type2d() { return "Video" };

    _initDefaultVars() {
        super._initDefaultVars();

        this._wh = new THREE.Vector2(0.4, 0.35);

        this._loading = true;
        this._duration = 0;
        this._src = null;
        this._videoCover = false;
        this._controlsEnabled = Q3.editMode;
        this._autoplay = true;
        this._loop = false;
        this._timeOnSlide = {};
        this._borderRadius = 0;
        this._volume = 1;
        this._playbackRate = 1;
    }

    get isPaused() { return this.video.paused; }
    get loading() { return this._loading; }
    set loading(val) { this._loading = val; }
    get animationDelay() {return this.options.animation && this.options.animation.name ? this.options.animation.delay : 0}
    get duration() { return this._duration; }
    set duration(val) { this._duration = val; }
    get src() { return this._src; }
    get isMp4Browser() { return Q3.browserCapabilities.isSafari }
    set src(val) { this._src = this.isMp4Browser ? val.replace(".webm", ".mp4") : val; }
    get videoCover() { return this._videoCover; }
    set videoCover(val) { this._videoCover = val; }
    get controlsEnabled() { return this._controlsEnabled; }
    set controlsEnabled(val) { this._controlsEnabled = val; }
    get autoplay() { return this._autoplay; }
    set autoplay(val) { this._autoplay = val; }
    get loop() { return this._loop; }
    set loop(val) { this._loop = val; }
    get timeOnSlide() { return this._timeOnSlide; }
    set timeOnSlide(val) { this._timeOnSlide = val; }
    get borderRadius() { return this._borderRadius; }
    set borderRadius(val) { this._borderRadius = val; }
    get volume() { return this._volume; }
    set volume(val) { this._volume = val; this.video.volume = val; }
    get playbackRate() { return this._playbackRate }
    set playbackRate(val) { this._playbackRate; this.video.playbackRate = val }
    get nonTransparent() { return this._src.split("?")[0].split(".").pop() === "mp4" && Boolean(this.data.nonTransparent) }
    set nonTransparent(val) { this.data.nonTransparent = val; }

    _initValues(options) {
        this._src = "src" in options
            ? "new" in options ? options.src : Q3.s3Url + options.src
            : this._src;

        if (this.isMp4Browser) {
            this._src = this._src.replace(".webm", ".mp4");

            if (!Q3.editMode && this.data.hasAlpha && !this._src.includes("_alpha.mp4"))
                this._src = this._src.replace(/.mp4$|.webm$/g, "_alpha.mp4");
        }

        this._videoCover = "videoCover" in options ? Boolean(options.videoCover) : this._videoCover;
        if (!Q3.editMode) {
            this._controlsEnabled = "controlsEnabled" in options ? Boolean(options.controlsEnabled) : this._controlsEnabled;
            this.disableEvents = this._controlsEnabled ? false : this.disableEvents;
        }
        this._volume = "volume" in options ? options.volume : this._volume;
        this._playbackRate = "playbackRate" in options ? options.playbackRate : this._playbackRate;
        this._autoplay = "autoplay" in options ? Boolean(options.autoplay) : true;
        this._loop = "loop" in options ? Boolean(options.loop) : false;
        this.overlaySeek = "overlaySeek" in options ? Boolean(options.overlaySeek) : false;

        if (this.overlaySeek) {
            this._autoplay = false;
            this._controlsEnabled = false;
        }

        this._timeOnSlide = "timeOnSlide" in options ? options.timeOnSlide : {};
        this._borderRadius = "borderRadius" in options ? options.borderRadius : this._borderRadius;
        this._sizeSm = options.sizeSm ? options.sizeSm : this._sizeSm;
    }

    _createContainer() {
        const container = super._createContainer();

        container.style.position = "absolute";

        return container;
    }

    constructor(options, parentLayer, uuid) {
        super(options, parentLayer, uuid);

        parentLayer.domElement.setAttribute("contains-video", "");

        //dom insertion
        this.videoWrap = document.createElement("div");
        this.videoElement = document.createElement("div");
        this.videoWrap.classList.add("iframe-wrap");
        this.videoWrap.appendChild(this.videoElement);

        if (this.overlaySeek) {
            this.seekEl = document.createElement("div");
            this.seekEl.classList.add("overlay-seeker");

            this.seekEl.onpointerdown = (e) => {
                e.preventDefault();

                let startPos = e.clientX / devicePixelRatio;
                let startTime = this.video.currentTime;

                const onPointerMove = (e) => {
                    e.preventDefault();

                    let delta = e.clientX / devicePixelRatio - startPos;
                    let deltaPercent = delta / this.seekEl.offsetWidth;
                    let deltaTime = this.duration * deltaPercent;
                    let targetTime = startTime + deltaTime;

                    if (targetTime < 0)
                        targetTime = this.duration + targetTime % this.duration;


                    if (targetTime > this.duration)
                        targetTime = targetTime % this.duration;

                    this.video.currentTime = targetTime;
                };

                const onPointerUp = () => {
                    e.preventDefault();

                    this.seekEl.onpointermove = null;

                    document.removeEventListener("pointermove", onPointerMove);
                    document.removeEventListener("pointerup", onPointerUp);
                };

                document.addEventListener("pointermove", onPointerMove);
                document.addEventListener("pointerup", onPointerUp);
            }
            this.videoWrap.appendChild(this.seekEl)
        }

        this.videoElement.classList.add("iframe-element");
        this.videoElement.classList.add("media-preloader");

        if (Q3.editMode) {
            this.videoElement.classList.add("editor-mode");

            if (Q3.videoProcessing[this.uuid])
                this.videoElement.classList.add("is-loading");
        }

        this.domElement.appendChild(this.videoWrap);

        this.parentLayer.subtype = "videos";

        if (Q3.videoPreload[this.uuid] && !Q3.editMode) {
            this.video = Q3.videoPreload[this.uuid];
            this.video.currentTime = 0;
            this.data.duration = this.video.duration;
            this.duration = this.video.duration;

            if (this.domElement) this.needsUpdate = true;
        } else {
            this.video = document.createElement("video");

            if (Q3.SlideManager.currentSlide.layers[this.parentLayer.uuid])
                this.video.src = this.src;

            this.video.setAttribute("crossorigin", "anonymous");

            const onLoadedMeta = () => {
                if (!this.video) return;

                this.data.duration = this.video.duration;
                this.duration = this.video.duration;

                if (this.domElement) this.needsUpdate = true;

                if (!Q3.editMode) {
                    this.video.removeEventListener("loadedmetadata", onLoadedMeta)
                } else {
                    Q3.Events.fire("slide_data_changed")
                }
            };

            this._onLoadedMeta = onLoadedMeta;
            this.video.addEventListener("loadedmetadata", onLoadedMeta);
        }

        this.video.setAttribute("playsinline", "");
        this.video.setAttribute("controlslist", "nodownload noremoteplayback nofullscreen");
        this.video.setAttribute("disablepictureinpicture", "");
        this.video.addEventListener("loadedmetadata", () => this.duration = this.video.duration);


        this.videoElement.appendChild(this.video);

        if (this.loop) this.video.setAttribute("loop", "loop");

        if (Q3.editMode && options.new) {
            this.videoElement.classList.add("is-loading");

            const srcChange = () => {
                if (this.video.src !== this.src) {
                    this.videoElement.classList.remove("is-loading");
                    this.video.removeEventListener("loadedmetadata", srcChange);
                    this.src = this.video.src;
                }
            };

            this._onSrcChange = srcChange;
            this.video.addEventListener("loadedmetadata", srcChange);
        }

        let disableAutoplaySlider = false;
        const layers = Q3.data.layers.layers2D;

        for (let l in layers) {
            if (layers.hasOwnProperty(l) && layers[l].subtype === "sliders") {
                const objects = layers[l].objects;

                for (let o in objects) {
                    if (objects.hasOwnProperty(o) && objects[o].type === "slider") {
                        for (let i = 0; i < (objects[o].action || []).length; i++) {
                            if (
                                objects[o].action[i].systemAction === "rewind" &&
                                objects[o].action[i].value.objectUuid === this.parentLayer.uuid
                            ) {
                                this.video.autoplay = false;
                                disableAutoplaySlider = true;
                            }
                        }
                    }
                }
            }
        }

        if (Q3.isReady) {
            this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
            this.enterExitCtrl.init(options);
        } else {
            Q3.Events.listen("tourReady", () => {
                this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
                this.enterExitCtrl.init(options);
            });
        }

        if (Q3.editMode) {
            // this.video.style.pointerEvents = "none";
            this.domElement.addEventListener("click", e => this.selectHandler(e), false);
        } else this.video.style.pointerEvents = this.disableEvents ? "none" : "auto";

        this.pause = this.pause.bind(this);
        this.delete = this.delete.bind(this);
        this.play = this.play.bind(this);
        this.redraw = this.redraw.bind(this);
        this.onSlideChange = this.onSlideChange.bind(this);
        this.setAnalyticsData = this.setAnalyticsData.bind(this);

        this.handleListeners(true);

        this.onMediaEnded = this.onMediaEnded.bind(this);

        if (!Q3.editMode) {
            if (this.data.hasAlpha && this.isMp4Browser) this.addSafariSupport();
            if (this.autoplay) {
                if (Q3.isReady) this.enableAutoplay(disableAutoplaySlider);
                else Events.listen("tourReady", () => this.enableAutoplay(disableAutoplaySlider));
            }

            let lastTime = this.video.currentTime;
            this._loopEndedCallback = () => {
                const curTime = this.video.currentTime;

                if (curTime < lastTime) this.onMediaEnded();

                lastTime = curTime;
            };

            this.video.addEventListener("ended", this.onMediaEnded);
            if (!disableAutoplaySlider && !this.overlaySeek) {
                this.video.addEventListener("timeupdate", this._loopEndedCallback);
            }
        }

        const action = this.data.action;

        if (!Q3.editMode && action && action[0]) {
            action.forEach(currAction => {
                const transition = new Transition(currAction.systemAction, currAction.value);

                if (currAction.userAction === "timeplayed")
                    transition.addTimePlayedAction(this.video, currAction.timePlayed);
                else if (currAction.userAction === "click")
                    this.domElement.addEventListener('click', () => transition.click(), false);
            });
        }
        this.updateControlsEnabled();
    }

    addSafariSupport() {
        this.removeSafariStuff = [];
        this.video.setAttribute("crossorigin", "anonymous");
        this.video.style.marginTop = "2px";

        const onDataLoaded = () => {
            let animId;
            let outputCanvas = document.createElement("canvas");
            let output = outputCanvas.getContext('2d');
            let bufferCanvas = document.createElement("canvas");
            let buffer = bufferCanvas.getContext('2d');
            let width = this.video.videoWidth / 2;
            let height = this.video.videoHeight;

            this.outputCanvas = outputCanvas;
            this.videoElement.insertBefore(outputCanvas, this.video);

            bufferCanvas.width = width * 2;
            bufferCanvas.height = height;
            outputCanvas.width = width;
            outputCanvas.height = height;

            const processFrame = () => {
                buffer.drawImage(this.video, 0, 0);

                // this can be done without alphaData, except in Firefox which doesn't like it when image is bigger than the canvas
                let image = buffer.getImageData(0, 0, width, height);
                let imageData = image.data;
                let alphaData = buffer.getImageData(width, 0, width, height).data;

                for (let i = 3, len = imageData.length; i < len; i = i + 4)
                    imageData[i] = alphaData[i - 1];

                output.putImageData(image, 0, 0, 0, 0, width, height);
            };

            const draw = () => {
                if (!this.video.paused) processFrame();

                animId = requestAnimationFrame(draw);
            };

            animId = requestAnimationFrame(draw);

            const stop = () => cancelAnimationFrame(animId);

            this.removeSafariStuff.push(() => {
                stop();
                this.videoElement.removeChild(outputCanvas);
                delete Q3.videoPreload[this.uuid];
                bufferCanvas.remove();
            });
        };

        if (this.video.duration) onDataLoaded();
        else this.video.addEventListener("loadedmetadata", onDataLoaded)
    }

    enableAutoplay(disableAutoplaySlider) {
        if (
            disableAutoplaySlider ||
            !SlideManager.currentSlide.layers[this.parentLayer.uuid] ||
            Q3.browserCapabilities.isMobile && this.hiddenMobile ||
            !Q3.browserCapabilities.isMobile && this.hiddenDesktop
        ) return;

        this.video.volume = this._volume;

        let animationDelay = this.animationDelay;

        const resumePlaybackInTeachMode = () => {
            if (Q3.teachMode === "student"
              && Q3.resumeTimestamp
              && Q3.SlideManager.currentSlide.uuid === Utils.queryParams.get("slide")) {
                this.video.currentTime = Q3.resumeTimestamp + (new Date().getTime() - window.performance.timing.navigationStart)/1000 - animationDelay;
            }
        }
        if (!animationDelay) {
            this.video.play();
            resumePlaybackInTeachMode()
        }
        else {
            this.video.style.pointerEvents = "none";
            this.isHiddenByAnimDelay = true;

            let animDelayTimeout;

            const onPause = () => clearTimeout(animDelayTimeout);
            const onResume = () => updateTimeout();

            const updateTimeout = () => {
                let timePlayed = this.enterExitCtrl ? this.enterExitCtrl.timePlayed : 0;
                animDelayTimeout = setTimeout(() => {
                    this.isHiddenByAnimDelay = false;
                    this.video.play();
                    resumePlaybackInTeachMode();
                    this.video.style.pointerEvents = this.disableEvents ? "none" : "auto";
                    Events.unlisten("autoplay_pause", onPause);
                    Events.unlisten("autoplay_resume", onResume);
                }, animationDelay * 1000 - timePlayed);
            };

            Events.listen("autoplay_pause", onPause);
            Events.listen("autoplay_resume", onResume);

            updateTimeout();
        }
        if (!this._loop) {
            Q3.mediaIsPlaying.push(this);
        }
    };


    toggleEvents(enabled) {
        this.disableEvents = !enabled;
        this.domElement.style.pointerEvents = enabled || Q3.editMode ? "auto" : "none";
    }

    onMediaEnded() {
        if (this._loop && !Q3.isPlaying) return;

        Q3.mediaIsPlaying.splice(Q3.mediaIsPlaying.indexOf(this), 1);

        this.video.removeEventListener("ended", this.onMediaEnded);
        if (this._loopEndedCallback) this.video.removeEventListener("timeupdate", this._loopEndedCallback);

        let leaveDuration = 0;

        if (this.leavingAnimation && this.leavingAnimation.name !== "none") {
            this.enterExitCtrl._setAnimationToExit();
            leaveDuration = this.leavingAnimation.duration * 1000;
        }

        setTimeout(() => {
            let totalDuration = this.duration + this.animationDelay + leaveDuration
            if (!Q3.mediaIsPlaying.length) Events.fire("mediaEnded", totalDuration);
            // if (this.domElement && !this._loop) this.domElement.style.visibility = "hidden";
        }, leaveDuration);
    }

    setAnalyticsData(e) {
        Q3.Analytics.add({
            type: 'videoevent',
            eventName: e.type,
            videoTime: this.video.currentTime,
            duration: this.video.duration,
            videoName: this.parentLayer.name
        });
    }

    handleListeners(add) {
        Events[add ? "listen" : "unlisten"]("viewportchanged", this.redraw);
        Events[add ? "listen" : "unlisten"]("splashremoved", this.redraw);
        if (!Q3.editMode) {
            Events[add ? "listen" : "unlisten"]("autoplay_pause", this.pause);
            Events[add ? "listen" : "unlisten"]("autoplay_resume", this.play);
        }

        const videoEvents = [
            "abort", "ended", "pause", "play", "seeked",
            "webkitfullscreenchange", "mozfullscreenchange", "fullscreenchange"
        ];

        videoEvents.forEach(eventName => {
            this.video[add ? "addEventListener" : "removeEventListener"](eventName, this.setAnalyticsData);
        });

    }

    onSlideChange(opts) {
        if (!this.parentLayer.visible) { this.pause(); return; }

        if (this.timeOnSlide[SlideManager.activeSlide] && opts.userAction) {
            this.video.currentTime = this.timeOnSlide[SlideManager.activeSlide].time;
        }

        if (SlideManager.activeSlide === 0 && !Q3.isReady) {
            Events["listen"]("splashremoved", this.play);
        } else if (Q3.isPlaying) this.play();

    }

    play() {
        if (Q3.editMode) console.trace();
        if (
            !Q3.isReady || !this.parentLayer.visible ||
            (Q3.browserCapabilities.isMobile && this.hiddenMobile) ||
            (!Q3.browserCapabilities.isMobile && this.hiddenDesktop) ||
            this.isHiddenByAnimDelay
        ) return;

        if (!Q3.editMode) {
            this.video.volume = this._volume;
            this.video.duration !== this.video.currentTime && this.video.play();
        } else this.video.play();
    }

    pause() { this.video.pause(); }

    playLeavingAnimation() { this.enterExitCtrl.playLeave(); }
    cancelLeavingAnimation() { this.enterExitCtrl.cancelLeave(); }

    onAnimationFinish() {
        if (Q3.mediaIsPlaying.indexOf(this) !== -1)
            this.onMediaEnded();
    }

    _calcPosition(width, height) {
        let y, x;

        x = this.x * width * RenderManager.pixelRatio;
        y = this.y * height * RenderManager.pixelRatio;

        return [x, y];
    }

    calcSize(w, h) {
        let { videoWidth, videoHeight } = this.video;
        let ratio = videoHeight / videoWidth;

        if (h > w * ratio) return { width: w, height: w * ratio };

        return { width: h / ratio, height: h };
    }

    setControlsEnabled(enabled) {
        this.controlsEnabled = enabled;
        this.updateControlsEnabled();
    }

    setLoop(enabled) {
        this.loop = enabled;

        if (this.loop) this.video.setAttribute("loop", "loop");
        else this.video.removeAttribute("loop");
    }

    updateControlsEnabled() {
        if (this.controlsEnabled) this.video.setAttribute("controls", "");
        else this.video.removeAttribute("controls");
    }

    setVideoUrl(value) {
        this.src = value;

        Events.fire("selectchange");
        value = value.replace(/allowfullscreen/g, "");

        this.video.src = value;

        return value;
    }

    onVisibilityChange(val) {
        if (val && !this.video.src)
            this.video.src = this.src;
    }

    redraw(width, height) {
        if (!width) width = RenderManager.domElement.width / RenderManager.pixelRatio;
        if (!height) height = (RenderManager.domElement.height) / RenderManager.pixelRatio;

        let currentSize = this.getVWSize();

        if (this[currentSize]) Object.assign(this, this[currentSize]);

        if (!Q3.editMode && this.inLetterbox !== false) {
            width = Q3.letterbox.offsetWidth / RenderManager.pixelRatio;
            height = (Q3.letterbox.offsetHeight) / RenderManager.pixelRatio;
        }

        if (Q3.editMode) {
            if (Q3.mobileView && this.hiddenMobile) this.domElement.style.opacity = 0.4;
            else if (!Q3.mobileView && this.hiddenDesktop) this.domElement.style.opacity = 0.4;
            else this.domElement.style.opacity = 1;
        }

        let w = this.width * width * RenderManager.pixelRatio;
        let h = this.height * height * RenderManager.pixelRatio;

        Object.assign(this.videoWrap.style, {
            width: w + "px",
            height: h + "px"
        });

        let size = this.calcSize(w, h);

        if (this.inLetterbox === false || this.videoCover) size = { width: w, height: h };

        let sizeCss = { width: size.width + "px", height: size.height + "px" };

        Object.assign(this.videoElement.style, sizeCss);

        let [x, y] = this._calcPosition(width, height);

        Object.assign(this.domElement.style, {
            width: w + "px",
            height: h + "px",
            top: y + "px",
            left: x + "px"
        });

        this.video.width = size.width;
        this.video.height = size.height;

        if (this.videoCover) Object.assign(this.video.style, { objectFit: "cover" });
        else Object.assign(this.video.style, { objectFit: "contain" });

        if (this.outputCanvas) {
            this.outputCanvas.style.width = size.width + "px";
            this.outputCanvas.style.height = size.height + "px";
            this.outputCanvas.style.objectFit = this.videoCover ? "cover" : "contain";
        }

        this.updateControlsEnabled();
        this.video.style.borderRadius = parseFloat(this.borderRadius) / 15 + "rem"
    }

    delete() {
        if (this.removeSafariStuff && this.removeSafariStuff.length)
            this.removeSafariStuff.forEach(fn => fn())

        this.handleListeners(false);

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);

        this.video.removeEventListener("ended", this.onMediaEnded);
        this.video.removeEventListener("ended", this._onEndedListener);
        if (this._loopEndedCallback) this.video.removeEventListener("timeupdate", this._loopEndedCallback);
        this.video.pause();

        this.video.remove();

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire("selectchange");
        }

    }

    clone() { }

    hide() { this.pictureElement.classList.add("q3-picture-occluded"); }
    show() { this.pictureElement.classList.remove("q3-picture-occluded"); }

    setData(data) {
        let currentSize = this.getVWSize();

        Object.assign(this[currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height
        });

        this.data[currentSize] = {};

        Object.assign(this.data[currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height
        });

        if (currentSize === "sizeSm") this.data.sizeSm.autoResized = true;

        this.redraw();
    }

    setCurrentTime(t) { this.video.currentTime = t; }
}

export default Video;
export { Video };
